import { useEffect, useState } from "react";

// react-router-dom components
import { NavLink, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import List from "@mui/material/List";

import SidenavItem from "./SidenavItem";
import SidenavList from "./SidenavList";
import SidenavRoot from "./SidenavRoot";

import MaterialBox from "../../../../components/MaterialBox";
import MaterialTypography from "../../../../components/MaterialTypography";
import { setMiniSidenav, useMaterialUIController } from "../../../../context";
import SidenavCollapse from "./SidenavCollapse";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography } from "@mui/material";
import FullScreenSliderDialog from "../../../../components/FullScreenSliderDialog";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const itemName = pathname.split("/").slice(1)[1];

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  
  const [openDialog, setOpenDialog] = useState(false);

  const [whatsNew] = useState([
    {
      id: "5",
      title: "Cancellation for WFH, Leave, and Permission",
      description: `This update introduces the ability for employees to cancel their previously submitted 
      Work From Home (WFH), Leave, or Permission requests. Whether the request is pending or already approved, 
      employees can now revoke it directly through the HRMS system. Upon cancellation, the request status 
      is updated in real time and an automatic notification is sent to the respective reporting manager, 
      ensuring that all stakeholders are informed without the need for manual follow-up.

The feature enhances flexibility for employees to make adjustments when plans change and ensures that 
attendance, leave, and permission records remain accurate. It also contributes to streamlined HR operations 
by reducing the need for manual data correction. Cancelled requests are tracked within the system, helping 
to maintain clean records for attendance reports, audits, and payroll processing. This update supports a 
more transparent and user-controlled workflow for managing time-off requests.`,
    },
    {
      id: "1",
      title: "Exit Formality",
      description: `The Exit Formality page is designed to streamline and standardize the employee offboarding 
      process within the HRMS system. It provides a centralized platform for HR personnel, reporting managers, 
      and department heads to efficiently manage all necessary exit procedures. This includes initiating resignation 
      workflows, handling departmental clearances, collecting exit feedback, processing final approvals, and 
      documenting asset returns.

By ensuring that every required step is completed in a structured and timely manner, this page helps 
minimize compliance risks, prevents delays in separation formalities, and maintains a professional exit 
experience for employees. It also improves internal coordination by allowing multiple stakeholders to track 
and update progress in real time, ensuring a smooth and transparent offboarding process.`,
    },
    {
      id: "3",
      title: "Manual LOP",
      description: `The Manual LOP (Loss of Pay) page provides HR and authorized personnel with the ability to manually record salary deductions for employees based on unapproved absences, policy violations, or special cases that fall outside the scope of the automated attendance tracking system. This feature ensures that payroll calculations remain accurate and compliant with company policies, even when exceptional scenarios arise.

Through this page, users can select specific employees, define the date range for LOP application, enter the number of days or hours to be deducted, and provide relevant remarks or reasons. The system immediately reflects the deduction in the payroll module, ensuring alignment between attendance records and salary processing.`,
    },
    {
      id: "4",
      title: "Task Management",
      description: `The Task Management module in the HRMS system is designed to enhance employee productivity and accountability by enabling the assignment, tracking, and completion of work-related tasks in an organized and collaborative environment. This feature allows managers to create and assign tasks to individuals or teams, set deadlines, define priorities, and monitor progress in real time.

Employees can view their assigned tasks, update statuses, add comments, and mark tasks as complete. Managers gain visibility into task performance, delays, and completion rates, enabling better workload distribution and timely decision-making. The system also supports task categorization, progress tracking, and historical logs for performance reviews and audit purposes.

Whether it's for routine work items, project deliverables, or ad-hoc assignments, the Task Management page provides a centralized workspace to ensure alignment, transparency, and timely execution of responsibilities across the organization.`,
    },
  ]);

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };

  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, isDevPage, collapse, route, href, key }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemName}
            open={openNestedCollapse === name}
            onClick={() =>
              openNestedCollapse === name
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(name)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else if (isDevPage === true) {
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      iconType,
      title,
      collapse,
      noCollapse,
      key,
      href,
      route,
      hidden,
    }) => {
      let returnValue;
      if (hidden) return null;
      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{
                textDecoration: "none",
              }}
            >
              <SidenavCollapse
                iconType={iconType}
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                iconType={iconType}
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              iconType={iconType}
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              noCollapse={noCollapse}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MaterialTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MaterialTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      }
      return returnValue;
    }
  );

  return (
    <>
      <FullScreenSliderDialog
        open={openDialog}
        onClose={handleCloseDialog}
        items={whatsNew}
      />
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      >
        <MaterialBox pt={2} pb={2} textAlign="center">
          <MaterialBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            // p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MaterialTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>
                <CloseIcon />
              </Icon>
            </MaterialTypography>
          </MaterialBox>
          <MaterialBox
            component={NavLink}
            to="/dashboard"
            display="flex"
            alignItems="center"
            justifyContent={miniSidenav ? "center" : "center"}
            sx={{
              textDecoration: "none",
              // padding: miniSidenav ? "10px" : "10px 20px",
              backgroundColor: "transparent",
              transition: "all 0.3s ease-in-out",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#e3f2fd", // Light blue hover effect
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow
              },
            }}
          >
            {/* Logo */}
            {brand && (
              <MaterialBox
                component="img"
                src={brand}
                alt="Logo"
                sx={{
                  width: miniSidenav ? "4rem" : "10rem",
                  height: "3rem",
                  objectFit: "contain",
                  margin: miniSidenav ? "0 auto" : "0 auto",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.1)", // Slight zoom on hover
                  },
                }}
              />
            )}
          </MaterialBox>
        </MaterialBox>
        <List>{renderRoutes}</List>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
            mt: "auto",
            cursor: "pointer",
          }}
          onClick={handleOpenDialog}
        >
          <Typography variant="overline" color="textPrimary">
            V.1.1.2
          </Typography>
        </Box>
      </SidenavRoot>
    </>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
